import { container, singleton } from "tsyringe";

import { BehaviorSubject } from 'rxjs';
import { ChatMessage, Participant } from '@bluejeans/webrtc-client-sdk';

import { WebRTCsdkService } from './webrtcsdk-service';
import { UserService } from './user-service';
import { PTApiService } from './ptapi-service';
import { PTSocketService } from './ptsocket.service';

import { MeetingConnectionState, PtMeeting } from '../models/meetingClass';
import { FullChatMessage, PTChatMessage } from '../models/ptChat';

import * as R from 'ramda';

// const Primus = require('../thirdparty/primus');
// import { Config } from '../config';

export enum CHATPANEL {
  PUBLIC = "PUBLIC",
  PRIVATE = "PRIVATE"
}

export enum PRIVATECHATVIEW {
  PARTICIPANTS = "PARTICIPANTS",
  CHAT = "CHAT"
}

@singleton()
export class ChatService {

  public clearHistory$: BehaviorSubject<ChatMessage> = new BehaviorSubject<ChatMessage>(undefined);

  public unreadBackstageCounter$ = new BehaviorSubject<number>(0);
  public unreadModeratorCounter$ = new BehaviorSubject<number>(0);
  public unreadPublicCounter$    = new BehaviorSubject<number>(0);
  public unreadDirectCounter$    = new BehaviorSubject<number>(0);

  public retryAfterSeconds$      = new BehaviorSubject<number>(0);

  public publicChatMessages$     = new BehaviorSubject<FullChatMessage[]>([]);
  public backstageChatMessages$  = new BehaviorSubject<FullChatMessage[]>([]);
  public moderatorChatMessages$  = new BehaviorSubject<FullChatMessage[]>([]);
  public privateChatMessages$    = new BehaviorSubject<FullChatMessage[]>([]);

  public currentOneToOneEndpointGuid$ = new BehaviorSubject<string>(null);


  chatPanelToShow: CHATPANEL = CHATPANEL.PUBLIC;
  privateChatViewToShow: PRIVATECHATVIEW = PRIVATECHATVIEW.PARTICIPANTS;
  selectedParticipant: Participant = null;

  private webRTCsdkService = container.resolve(WebRTCsdkService);
  private userService = container.resolve(UserService);
  private ptApiService = container.resolve(PTApiService);
  private ptSocketService = container.resolve(PTSocketService);

  private initialized = false;
  private hideInterval;

  constructor() {
    if (!this.initialized) {
      this.initialize();
    }

  }

  sortOnTimestamp(a, b) {
    return a.timestamp - b.timestamp;
  }

  async initialize() {

    this.initialized = true;

    // TODO  check if we need this auto hide on timer
    // this.hideInterval = setInterval(() => {
    //   this.hideExpirePublicChats();
    // }, 5000);

    // add observable
    this.ptSocketService.newMsgChatObservable.subscribe((msg) => this.handleChatNewMsg(msg));
    this.ptSocketService.deleteMsgChatObservable.subscribe((msg) => this.handleChatDeleteMsg(msg));
    this.ptSocketService.limitedRateChatObservable.subscribe((msg) => this.handleChatRateLimited(msg));


    // clear history and newmsg queues
    // this.clearHistory$.next(null);

    const user = this.userService.user;

    // public chat
    const publics = await this.ptApiService.getGeneralChatHistory();

    console.log('get public chat msg', publics);

    const sorted = publics.sort(this.sortOnTimestamp).map(m => Object.assign({ ...m, isUnread: true, onStreamDate: false, deleteDate: 0, isSelf: m.sender.userId === user.id }))
    console.log('get public chat msg', publics, sorted);
    this.publicChatMessages$.next(sorted);

    setTimeout(() => {
      this.unreadPublicCounter$.next(this.publicChatMessages$.getValue().length);
    }, 500);


    // backstage / host chat
    const backstages = []; // await this.ptApiService.getBackstageChatHistory();
    this.backstageChatMessages$.next(backstages.sort(this.sortOnTimestamp).map(m => Object.assign({ ...m, isUnread: true, isSelf: m.sender.userId === user.id })));
    setTimeout(() => {
      const unreadBackstage = this.backstageChatMessages$.getValue().filter(m => m.isUnread);
      this.unreadBackstageCounter$.next(unreadBackstage ? unreadBackstage.length : 0);
    }, 500);

    // moderators / host chat
    const moderators = []; // await this.ptApiService.getModeratorChatHistory();
    this.moderatorChatMessages$.next(moderators.sort(this.sortOnTimestamp).map(m => Object.assign({ ...m, isUnread: true, isSelf: m.sender.userId === user.id })));
    setTimeout(() => {
      const unreadModerator = this.moderatorChatMessages$.getValue().filter(m => m.isUnread);
      this.unreadModeratorCounter$.next(unreadModerator ? unreadModerator.length : 0);
    }, 500);

    const directs = await this.ptApiService.getLastPrivateMessages();
    this.privateChatMessages$.next(directs.sort(this.sortOnTimestamp).map(m => Object.assign({ ...m, isUnread: true, isSelf: m.sender.userId === user.id })));

    this.countUnreadMessages();
  }

  // TODO uncomment if auto hide after a delai is wanted back
  // hideExpirePublicChats() {

  //   const maxDisplayDelay = 20000;  // 20sec  TODO set right value here
  //   const now = Date.now();

  //   let updated = false;
  //   const publicMsgR = R.clone(this.publicChatMessages$.getValue());
  //   for(const msg of publicMsgR) {
  //     if (msg.onStreamDate + maxDisplayDelay < now) {
  //       msg.onStreamDate = 0;
  //       updated = true;
  //     }
  //   }
  //   if (updated) {
  //     this.publicChatMessages$.next(publicMsgR);
  //   }

  // }

  handleChatDeleteMsg(msgId): void {
    if (!msgId) return;

    // set deletedDate in queue and delete after 2sec only

    const publicMsgR = R.clone(this.publicChatMessages$.getValue());
    const publicDeleted = publicMsgR.find(m => m.id === msgId);
    if (publicDeleted) {
      // console.log('DELETE chat found', deleted)
      publicDeleted.deleteDate = Date.now();
      this.publicChatMessages$.next((publicMsgR));
      setTimeout(() => {
        const publicMsgR = R.clone(this.publicChatMessages$.getValue());
        const now = Date.now();
        this.publicChatMessages$.next(publicMsgR.filter(m => m.deleteDate == 0 || (m.deleteDate + 1900) > now));
      }, 2000);
    }

    // TODO if uncommnented then ADD also MODERATOR
    // const backstageMsgR = R.clone(this.publicChatMessages$.getValue());
    // const backstageDeleted = backstageMsgR.find(m => m.id === msgId);
    // if (backstageDeleted) {
    //   // console.log('DELETE chat found', deleted)
    //   backstageDeleted.deleteDate = Date.now();
    //   this.backstageChatMessages$.next((publicMsgR));
    //   setTimeout(() => {
    //     const backstageMsgR = R.clone(this.publicChatMessages$.getValue());
    //     const now = Date.now();
    //     this.backstageChatMessages$.next(backstageMsgR.filter(m => m.deleteDate == 0 || (m.deleteDate + 1900) > now));
    //   }, 2000);
    // }

  }


  // relay send to ptsocket service
  sendChatMessage(body: string, channel: 'backstage' | 'moderator' | 'private' | 'public' = 'moderator', receiver: { guid: string, name: string, role: 'attendee' } = null) {
    this.ptSocketService.sendChatMessage(body, channel, receiver);
  }

  handleChatNewMsg(data): void {

    if (!data) return;

    // const user = this.userService.user;
    let sentBySelf = true;
    // if (data.sender.userId === user.id.toString()) {
    //   sentBySelf = true;
    // }
    const message = { ...data, isSelf: sentBySelf, isUnread: true, onStreamDate: false, deleteDate: 0};

    console.log('chat new', message);
    if (message.moderatorChat) {
      // only moderator / presenter
      this.moderatorChatMessages$.next([...this.moderatorChatMessages$.getValue().filter(m => m.id !== message.id), message]);
    } else if (message.moderatorPresenterChat) {
      // only moderator / presenter
      this.backstageChatMessages$.next([...this.backstageChatMessages$.getValue().filter(m => m.id !== message.id), message]);
    } else if (message.privateChat) {
      // private msg with moderator
      this.privateChatMessages$.next([...this.privateChatMessages$.getValue().filter(m => m.id !== message.id), message]);
    } else {
      // for all message
      this.publicChatMessages$.next([...this.publicChatMessages$.getValue().filter(m => m.id !== message.id), message]);
    }

    setTimeout(() => {
      this.countUnreadMessages();
    }, 500)
  }

  countUnreadMessages() {
    const unreadBackstage = this.backstageChatMessages$.getValue().filter(m => m.isUnread).length;
    this.unreadBackstageCounter$.next(unreadBackstage);

    const unreadModerator = this.moderatorChatMessages$.getValue().filter(m => m.isUnread).length;
    this.unreadModeratorCounter$.next(unreadModerator);

    const unreadPublic = this.publicChatMessages$.getValue().filter(m => m.isUnread).length;
    this.unreadPublicCounter$.next(unreadPublic);

    const unreadDirectCounter = this.privateChatMessages$.getValue().filter(m => m.isUnread).length;
    this.unreadDirectCounter$.next(unreadDirectCounter);
  }

  handleChatRateLimited(data): void {
    if (!data) return;

    console.log('Got Rate limit retry time as - ' + data.body.retryAfterSeconds);
    const retryAfterSeconds = data?.body?.retryAfterSeconds;
    if (!isNaN(retryAfterSeconds)) {
      this.retryAfterSeconds$.next(retryAfterSeconds);
    } else {
      console.log('ERROR: Rate limit retry is not a number');
    }
  }


  resetUnreadPublicChat(): void {
    const publicMsg = this.publicChatMessages$.getValue();
    publicMsg.forEach(m => m.isUnread = false);
    this.publicChatMessages$.next(publicMsg);
    this.countUnreadMessages();
  }

  resetUnreadBackstageChat(): void {
    const backstageMsg = this.backstageChatMessages$.getValue();
    backstageMsg.forEach(m => m.isUnread = false);
    this.backstageChatMessages$.next(backstageMsg);
    this.countUnreadMessages();
  }

  resetUnreadModeratorChat(): void {
    const moderatorMsg = this.moderatorChatMessages$.getValue();
    moderatorMsg.forEach(m => m.isUnread = false);
    this.moderatorChatMessages$.next(moderatorMsg);
    this.countUnreadMessages();
  }

  deleteMsg(id): void {
    this.ptApiService.deleteChatMessage(id);
  }

}
